import {
  ExponentialBackoff,
  handleAll,
  handleWhen,
  retry,
  type RetryPolicy,
} from "cockatiel";

import { isNetworkError } from "../errors";
import { LoggerService } from "../services/LoggerService";

import { RobotoUnauthorizedException } from "./exceptions";
import { HttpResponse } from "./HttpResponse";

/**
 * The token vendor returned nothing.
 *
 * Its own type so the retry policy can tell it apart from a programming error.
 * The customer may still be signed in: `Amplify.configure` resets the Cognito
 * token provider to its default storage, so a read landing between that reset
 * and `setKeyValueStorage` finds an empty store. Retrying costs a few hundred
 * milliseconds; not retrying costs the session.
 */
class MissingAuthTokenError extends Error {
  constructor() {
    super("No auth token returned from vendor");
    this.name = "MissingAuthTokenError";
  }
}

/** Narrows before `instanceof`, which rejects an `unknown` left-hand side. */
function isMissingAuthToken(error: unknown): boolean {
  return error instanceof Error && error instanceof MissingAuthTokenError;
}

export enum HttpStatus {
  REQUEST_TIMEOUT = 408,
  TOO_MANY_REQUESTS = 429,
  INTERNAL_SERVER_ERROR = 500,
  BAD_GATEWAY = 502,
  SERVICE_UNAVAILABLE = 503,
  GATEWAY_TIMEOUT = 504,
}

export type BearerTokenVendor = () => Promise<string | undefined>;

interface ConstructorArgs {
  baseHeaders: Headers;
  baseUrl: URL;
  bearerTokenVendor: BearerTokenVendor;
  onAuthFailure: () => Promise<void>;
  /** Whether to retry idempotent requests on transient failures. Defaults to true. */
  retry: boolean;
}

/**
 * Range bounds are inclusive and start at 0
 * https://www.rfc-editor.org/rfc/rfc9110#section-14.1.2-2
 */
interface Range {
  unit: "bytes";
  start?: number;
  end?: number;
}

interface RequestOptions extends Omit<RequestInit, "method"> {
  /**
   * Whether the server acting on this request twice is the same as acting on it once
   * (default true). Pass false for requests that must not replay — creates that mint an id,
   * deletes whose success turns into not-found — and the client makes exactly one attempt,
   * whatever the failure.
   */
  idempotent?: boolean;
  /** Full replacement for the retry policy; when set, `idempotent` is not consulted. */
  retryPolicy?: RetryPolicy;
  excludeAuth?: boolean;
}

interface GetRequestOptions extends RequestOptions {
  ranges?: Range[];
}

/**
 * A URL instance or url-like string.
 * If a string, the value will be turned into a URL instance and made relative to HttpClient::baseUrl (if provided)
 * with a call to HttpClient::constructUrl.
 */
type URLLike = URL | string;

/**
 * Lightweight facade around `fetch`, with built-in support for:
 *  - Authorizing requests (bearer token auth)
 *  - retry
 */
export class HttpClient {
  #baseHeaders?: Headers;
  #baseUrl?: URL;

  /**
   * Vendor for bearer tokens to authenticate requests with.
   *
   * @throws {Error} If the bearer token vendor fails to provide a token
   */
  #bearerTokenVendor?: BearerTokenVendor;
  #onAuthFailure?: () => Promise<void>;
  #authFailureInProgress = false;
  #retry: boolean;

  constructor(options?: Partial<ConstructorArgs>) {
    this.#baseHeaders = options?.baseHeaders;
    this.#baseUrl = options?.baseUrl;
    this.#bearerTokenVendor = options?.bearerTokenVendor;
    this.#onAuthFailure = options?.onAuthFailure;
    this.#retry = options?.retry ?? true;
  }

  public constructUrl = (
    relativeUrl: string,
    searchParams?: URLSearchParams,
  ): URL => {
    // Note: searchParams.size doesn't work on Safari 16 (17+ is fine)
    // This left out page_token in URLs resulting in an infinite loop
    const hasSearchParams =
      searchParams !== undefined && Array.from(searchParams).length > 0;
    let normalizedRelativePath = hasSearchParams
      ? `${relativeUrl}?${searchParams.toString()}`
      : relativeUrl;

    // This exists for base URLs which don't just end in '/', specifically, API Gateway autogenerated stage URLs
    // which look like https://qa9bevzouk.execute-api.us-gov-west-1.amazonaws.com/prod
    // Without this, the "/prod" would get dropped from the path
    if (this.#baseUrl?.pathname && this.#baseUrl.pathname !== "/") {
      normalizedRelativePath = `${this.#baseUrl.pathname}/${normalizedRelativePath}`;
    }

    return new URL(normalizedRelativePath, this.#baseUrl);
  };

  public delete = (
    url: URLLike,
    options: RequestOptions = {},
  ): Promise<HttpResponse> => {
    return this.#request({
      method: "DELETE",
      url,
      options,
    });
  };

  public get = (
    url: URLLike,
    options: GetRequestOptions = {},
  ): Promise<HttpResponse> => {
    return this.#request({
      method: "GET",
      url,
      options,
    });
  };

  public post = (
    url: URLLike,
    options: RequestOptions = {},
  ): Promise<HttpResponse> => {
    return this.#request({
      method: "POST",
      url,
      options,
    });
  };

  public put = (
    url: URLLike,
    options: RequestOptions = {},
  ): Promise<HttpResponse> => {
    return this.#request({
      method: "PUT",
      url,
      options,
    });
  };

  #request = async ({
    method,
    url,
    options,
  }: {
    method: string;
    url: URLLike;
    options: RequestOptions & GetRequestOptions;
  }) => {
    const headers = this.#buildHeaders(options.headers);

    if (!options.excludeAuth) {
      try {
        const bearerToken = await this.#getAuthToken(
          options.signal ?? undefined,
        );
        if (bearerToken) {
          headers.append("Authorization", `Bearer ${bearerToken}`);
        }
      } catch (error) {
        LoggerService.error("Failed to get auth token", error);

        if (!this.#onAuthFailure) {
          // Programming error. Auth failure handler must be provided if auth is not excluded
          throw new Error("No auth failure handler provided");
        }

        if (!this.#authFailureInProgress) {
          this.#authFailureInProgress = true;
          await this.#onAuthFailure().finally(() => {
            this.#authFailureInProgress = false;
          });
        }
        return new HttpResponse(
          new Response(
            JSON.stringify({
              error: {
                error_code: RobotoUnauthorizedException.name,
                message: "Failed to authenticate request",
                cause: error,
              },
            }),
            {
              status: 401,
              statusText: "Unauthorized",
              headers: { "Content-Type": "application/json" },
            },
          ),
        );
      }
    }

    if (options.ranges && options.ranges.length > 0) {
      const unit = options.ranges[0].unit;
      if (options.ranges.some((range) => range.unit !== unit)) {
        // All ranges must have the same unit
        throw new Error("All HTTP ranges must have the same unit");
      }
      if (
        options.ranges.some(
          (range) => range.start === undefined && range.end === undefined,
        )
      ) {
        throw new Error(
          "HTTP range must specify a start or end offset, or both",
        );
      }
      const ranges = options.ranges.map((range) => {
        // E.g. "0-1", "-500", "9500-"
        return `${range.start ?? ""}-${range.end ?? ""}`;
      });
      headers.append("Range", `${unit}=${ranges.join(", ")}`);
    }

    const normalizedUrl = url instanceof URL ? url : this.constructUrl(url);

    const request = new Request(new URL(normalizedUrl, this.#baseUrl), {
      body: options.body,
      credentials: options.credentials,
      headers: this.#mergeWithBaseHeaders(headers),
      integrity: options.integrity,
      keepalive: options.keepalive,
      method,
      mode: options.mode,
      redirect: options.redirect,
      referrer: options.referrer,
      referrerPolicy: options.referrerPolicy,
      signal: options.signal,
      window: options.window,
    });
    const retryPolicy =
      options.retryPolicy ??
      this.#retryPolicyFactory(options.idempotent ?? true);

    // Fetch only allows you to use a request once, so without calling clone, retry attempts will fail non-gracefully
    const responsePromise = (): Promise<Response> => {
      return fetch(request.clone());
    };

    const response = await retryPolicy.execute<Response>(
      responsePromise,
      request.signal,
    );

    return new HttpResponse(response);
  };

  #getAuthToken = async (signal?: AbortSignal): Promise<string> => {
    const authTokenRetryPolicy = retry(
      // Empty reads are retried alongside network errors. An empty read is not
      // proof of a signed-out customer: the token store is briefly unreadable
      // while Amplify is reconfigured, and the caller that lands in that window
      // would otherwise take the whole session down with it — see
      // `MissingAuthTokenError`. A genuinely absent token simply fails every
      // attempt and still reaches `onAuthFailure`.
      handleWhen((error) => isNetworkError(error) || isMissingAuthToken(error)),
      {
        backoff: new ExponentialBackoff({
          maxDelay: 5_000, // 5s max delay for auth tokens
        }),
        maxAttempts: 3,
      },
    );

    const bearerToken = await authTokenRetryPolicy.execute(async () => {
      if (this.#bearerTokenVendor === undefined) {
        // Programming error, and deliberately not a `MissingAuthTokenError`:
        // retrying cannot conjure a vendor that was never supplied.
        throw new Error("No bearer token vendor provided");
      }

      const token = await this.#bearerTokenVendor();

      if (!token) {
        throw new MissingAuthTokenError();
      }

      return token;
    }, signal);

    return bearerToken;
  };

  #buildHeaders = (headersInit?: HeadersInit): Headers => {
    const headers = new Headers();
    if (headersInit === undefined) {
      return headers;
    }

    if (headersInit instanceof Headers) {
      headersInit.forEach((value, key) => {
        headers.set(key, value);
      });
    } else if (Array.isArray(headersInit)) {
      headersInit.forEach(([key, value]) => {
        headers.set(key, value);
      });
    } else if (headersInit !== undefined) {
      Object.entries(headersInit).forEach(([key, value]) => {
        headers.set(key, value);
      });
    }
    return headers;
  };

  #mergeWithBaseHeaders = (headers: Headers): Headers => {
    const merged = new Headers(this.#baseHeaders);
    headers.forEach((value, key) => {
      merged.set(key, value);
    });
    return merged;
  };

  #retryPolicyFactory = (idempotent: boolean): RetryPolicy => {
    // A non-idempotent request gets exactly one attempt.
    if (!idempotent || !this.#retry) {
      return retry(handleAll, { maxAttempts: 0 });
    }

    const isTransientHttpError = (res: unknown): boolean => {
      if (!(res instanceof Response)) {
        return false;
      }

      return [
        HttpStatus.REQUEST_TIMEOUT,
        HttpStatus.INTERNAL_SERVER_ERROR,
        HttpStatus.TOO_MANY_REQUESTS,
        HttpStatus.BAD_GATEWAY,
        HttpStatus.SERVICE_UNAVAILABLE,
        HttpStatus.GATEWAY_TIMEOUT,
      ].includes(res.status);
    };

    return retry(
      handleWhen(isNetworkError).orWhenResult(isTransientHttpError),
      {
        backoff: new ExponentialBackoff({
          maxDelay: 10_000, // 10s in ms
        }),
        maxAttempts: 5,
      },
    );
  };
}
